// The QueryClient both hosts and the tests make.
//
// Promise: data is fresh for 30 s and refetched when the window comes back;
// a 4xx is not retried (it will not change by trying again), nor a server that
// says it cannot reach the data now (`main_unreachable`: the offline edition
// on another PC's data; the event stream reads everything again when it is
// back), anything else up to three times; mutations are not retried. Tests pass `retry: false`.
// A key just found missing or forbidden (404 / 403) is not asked again for
// 30 s: asking again on mount (back, forward) clears the error while it is
// out, and the screen showed the list with nothing selected for a moment
// before "not found" came back. A query's own `enabled` takes over this.
import { type Query, QueryClient } from "@tanstack/react-query";
import { ApiError } from "./api";

/** How long a 404 / 403 is used as it is, without asking again. */
const UNAVAILABLE_RECHECK_MS = 30_000;

/** Whether the query just came back 404 / 403 (and is not asked again yet). */
export function recentlyUnavailable(query: Pick<Query, "state">): boolean {
  const { status, error, errorUpdatedAt } = query.state;
  return (
    status === "error" &&
    error instanceof ApiError &&
    (error.status === 404 || error.status === 403) &&
    Date.now() - errorUpdatedAt < UNAVAILABLE_RECHECK_MS
  );
}

export function createQueryClient(options: { retry?: boolean } = {}) {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        enabled: (query) => !recentlyUnavailable(query),
        refetchOnWindowFocus: true,
        retry:
          options.retry === false
            ? false
            : (count, error) =>
                !(
                  error instanceof ApiError &&
                  ((error.status >= 400 && error.status < 500) ||
                    error.code === "main_unreachable")
                ) && count < 3,
      },
      mutations: { retry: false },
    },
  });
}
